/**
 * SIDE_PANELS registry — declarative list of collapsible panels rendered in
 * the right-side column during chat mode (see UI_TAXONOMY.md §4, §6).
 *
 * To add a new side panel:
 *   1. Create the component — it receives `{ value: unknown }`.
 *   2. Push an entry into SIDE_PANELS below.
 *   3. Done. No App.tsx edits needed.
 */

import type { ComponentType } from 'react';
import type { AppState } from "@strands/agent-transport";
import { TodoPanel } from "@/modules/chat/components/panels/TodoPanel";
import { SkillsPanel } from "@/modules/chat/components/panels/SkillsPanel";
import { FilesPanel } from "@/modules/chat/components/panels/FilesPanel";

export type SidePanelSource = `state:${string}` | `rest:${string}` | 'static';

/**
 * Props passed to every side panel component by SideStack.
 *
 * `value` is resolved from the panel's `source` (for `state:` sources it's
 * `appState[key]`; for `rest:`/`static` it's `undefined` — the panel is
 * expected to manage its own data).
 *
 * `state` is the full AG-UI session state, always available. Panels that
 * combine multiple sources (e.g. SkillsPanel reads a REST list AND watches
 * state.skills.recent for highlighting) use it.
 *
 * `sessionId` is the active chat/session id, or null in hero mode. Panels
 * that call session-scoped REST endpoints (download/delete/upload) need it;
 * pure state-driven panels (TodoPanel) can ignore it.
 */
export interface SidePanelProps {
  value: unknown;
  state: AppState;
  sessionId: string | null;
}

export interface SidePanelEntry {
  /** Unique key, used as React key and for debugging. */
  id: string;
  /** Display title (metadata — the component usually renders its own summary). */
  title: string;
  /**
   * Where the data comes from:
   *  - `state:<key>` — reads `appState[key]` reactively (AG-UI STATE_SNAPSHOT/DELTA)
   *  - `rest:<url>`  — panel fetches data itself from the URL on mount
   *  - `static`      — panel manages its own data (hardcoded list, local state, etc.)
   */
  source: SidePanelSource;
  /** The panel component. Receives `{ value, state }`. */
  component: ComponentType<SidePanelProps>;
  /** If provided, hides the panel when the predicate returns false. */
  showWhen?: (value: unknown) => boolean;
  /** Sort order within the side stack. Lower = higher. Default 0. */
  order?: number;
}

// ─── Registry ────────────────────────────────────────────────────

export const SIDE_PANELS: SidePanelEntry[] = [
  {
    id: 'plan',
    title: 'Прогресс',
    source: 'state:plan',
    component: TodoPanel,
    // No showWhen — the panel renders its own empty-state placeholder,
    // so it stays visible as a UX anchor (see UI_TAXONOMY.md §4).
    order: 0,
  },
  {
    id: 'files',
    title: 'Файлы',
    source: 'state:files',
    component: FilesPanel,
    order: 5,
  },
  {
    id: 'skills',
    title: 'Навыки',
    // REST source — the URL is metadata; the panel fetches itself on mount.
    // Highlight is driven by state.skills.recent via the `state` prop.
    source: 'rest:/api/skills',
    component: SkillsPanel,
    order: 10,
  },
];
